// Tiny pub/sub that bridges the Your Fit card (a question tap in
// EnamorInlineCustomize) to the turntable's FitHotspots overlay. Kept as a
// module-level singleton so the two sibling components don't need a shared
// React context. POC scope: MI10 sports-bra fit callouts.

const listeners = new Set();
let current = null;

// `current` is the FULL set of pins to render on the body (all shown at once).
export function emitHotspot(sequence) {
  current = sequence;
  listeners.forEach((l) => l(sequence));
}
export function subscribeHotspot(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function getHotspot() {
  return current;
}

// The single "triggered" hotspot — the one whose description box is shown. Set
// by a chat answer (the feature it talks about) or by the shopper tapping a pin.
// Independent of the pin set, so triggering a callout never hides the other pins.
let activeHot = null;
const activeListeners = new Set();

export function setActiveHotspot(step) {
  activeHot = step || null;
  activeListeners.forEach((l) => l(activeHot));
}
export function subscribeActiveHotspot(fn) {
  activeListeners.add(fn);
  return () => activeListeners.delete(fn);
}
export function getActiveHotspot() {
  return activeHot;
}

// Whether hotspots are currently ON (the toggle is on AND we're showing pins on a
// fit screen). The turntable reads this to hide its size bubble + spin CTA and to
// lock free-rotation while the hotspots cycle; when OFF, controls show and the
// shopper can spin freely.
let hotspotsShown = false;
const shownListeners = new Set();
export function setHotspotsShown(v) {
  v = !!v;
  if (v === hotspotsShown) return;
  hotspotsShown = v;
  shownListeners.forEach((l) => l(hotspotsShown));
}
export function subscribeHotspotsShown(fn) {
  shownListeners.add(fn);
  return () => shownListeners.delete(fn);
}
export function getHotspotsShown() {
  return hotspotsShown;
}

// Re-arm the turntable's intro lifecycle (one full rotation → hotspots on). Fired
// when the shopper enters a screen that should replay it (e.g. the Your Fit card),
// so the same load → rotate → hotspots-on flow runs there too.
let introTick = 0;
const introListeners = new Set();
export function triggerIntro() {
  introTick += 1;
  introListeners.forEach((l) => l(introTick));
}
export function subscribeIntro(fn) {
  introListeners.add(fn);
  return () => introListeners.delete(fn);
}

// Whether the shopper is on the Your Fit card. Hotspots (and the toggle) only
// show there; everywhere else the overlay is fully hidden. Leaving Your Fit also
// clears any active spotlight so it never lingers on another tab.
let fitActive = false;
const fitListeners = new Set();

export function setFitActive(v) {
  fitActive = !!v;
  if (!fitActive) {
    current = null;
    listeners.forEach((l) => l(null));
    activeHot = null;
    activeListeners.forEach((l) => l(null));
    setHotspotsShown(false);
  }
  fitListeners.forEach((l) => l(fitActive));
}
export function subscribeFitActive(fn) {
  fitListeners.add(fn);
  return () => fitListeners.delete(fn);
}
export function getFitActive() {
  return fitActive;
}

// A request from the host page (a marquee-CTA question tap) to open Your Fit and
// auto-ask a specific question, so the shopper doesn't re-pick it inside. Payload
// carries the question index (position in the product's Your Fit question set).
let fitAskReq = null;
const askReqListeners = new Set();
export function requestFitAsk(payload) {
  fitAskReq = payload || null;
  askReqListeners.forEach((l) => l(fitAskReq));
}
export function subscribeFitAsk(fn) {
  askReqListeners.add(fn);
  return () => askReqListeners.delete(fn);
}
export function getFitAsk() {
  return fitAskReq;
}

// One consistent hotspot accent. Kept simple: a near-black marker, no per-feature
// colour and no glow — clean and legible on any garment.
export const HOTSPOT_ACCENT = "#141414";

// Feature anchor builders. `tag` stays 3–4 words (the frosted card headline);
// `note` is one short benefit sentence (the card subtitle).
// cupsTop = the coverage line where the bust starts (for gaping/coverage);
// cupsApex = the CENTRE of the cup where the bust lies (for cup depth/padding);
// strap = the shoulder strap; band = underband; back = racerback.
const cupsTop = (angle, tag, note) => ({ feature: "cups", sub: "coverage", angle, tag, note });
const cupsApex = (angle, tag, note) => ({ feature: "cups", sub: "apex", angle, tag, note });
const strap = (angle, tag, note) => ({ feature: "cups", sub: "strap", angle, tag, note, place: "right" });
const band = (angle, tag, note, sub = "center") => ({ feature: "underband", sub, angle, tag, note });
const back = (angle, tag, note) => ({ feature: "edge", sub: "center", angle, tag, note }); // racerback

// Explicit sequence per MI10 (217) question, matched on the stable part of the
// phrasing ({cup}/{size} tokens are substituted at runtime). Ordered
// most-specific first; every question maps to a tailored spotlight.
const QUESTION_MAP = [
  // shape / gaping — coverage line + soft band edge
  [/right for my shape|suitable for my shape|gap/, () => [
    cupsTop(0, "Full bust coverage", "Cup sits high on the chest — smooth, no gaping."),
    band(0, "Soft, not sharp", "The band edge stays soft against the ribcage.", "lower"),
  ]],
  // caution — the wirefree band contact
  [/careful|watch out|downside/, () => [
    band(0, "Wirefree, even pressure", "No wire — the band spreads pressure evenly."),
  ]],
  // high-impact support — broad band, then the racerback shares the load
  [/high[- ]?impact|support/, () => [
    band(0, "Broad supportive band", "A wide band anchors the support, not the straps."),
    back(180, "Racerback shares load", "The racerback spreads the pull across your back."),
  ]],
  // activity fit — wirefree flex for cardio
  [/yoga|hiit|cardio|workout|run|gym/, () => [
    band(0, "Steady wirefree band", "A steady, flexible band that moves with you."),
  ]],
  // snug / bounce control — band snugness front, then full wrap
  [/snug|bounce|hold me in|slip/, () => [
    band(0, "Snug, bounce control", "A snug band keeps bounce in check."),
    band(180, "Wraps around fully", "Wraps fully around the ribcage so nothing slips."),
  ]],
  // cup vs smaller cup — deeper cup depth, band takes weight, straps work harder
  [/what changes|vs a smaller|bigger cup|larger cup/, () => [
    cupsApex(0, "Deeper cup depth", "Moulded deeper to hold the extra volume."),
    band(0, "Band takes weight", "The broad band carries most of the weight."),
    strap(0, "Straps work harder", "Straps fine-tune lift once the band is set."),
  ]],
  // what to expect at this cup — deep cups + supportive band
  [/what should i expect|what.*expect|expect at/, () => [
    cupsApex(0, "Deep padded cups", "Deep moulded cups shape and hold the bust."),
    band(0, "Broad supportive band", "A wide band anchors the support."),
  ]],
];

export function sequenceForQuestion(q) {
  const t = (q || "").toLowerCase();
  const hit = QUESTION_MAP.find(([re]) => re.test(t));
  if (hit) return hit[1]();
  // Fallback for any unmapped phrasing: coverage read.
  return [
    cupsTop(0, "Full bust coverage", "Cup sits high on the chest — smooth, no gaping."),
    band(0, "Soft, not sharp", "The band edge stays soft against the ribcage.", "lower"),
  ];
}

// Answer-driven hotspots. The Your Fit answers are pre-generated per shape AND
// cup, so the same question emphasises different features for different bodies
// (e.g. a bell answer talks about the top edge + underband; a round answer about
// cup depth + band snugness). We read the actual answer text and highlight the
// features it mentions, in the order it mentions them — so the callout always
// matches what the shopper is reading. Each cue maps to a manifest anchor that
// every MI10 body carries.
const ANSWER_FEATURES = [
  { re: /gap|gapp|spill|top[- ]?bust|upper[- ]?cup|upper[- ]?bust|upper[- ]?lift|coverage|neckline|seam|empty space|containment|\bedge\b/,
    step: () => ({ feature: "cups", sub: "coverage", angle: 0, tag: "Top coverage", note: "Coverage at the upper cup edge." }) },
  { re: /padded cup|padding|padded|cup depth|\bdepth\b|\bdeep\b|cradle|encase|compress|moulded|molded|projection|scaffold|grip|wirefree|unrestricted/,
    step: () => ({ feature: "cups", sub: "apex", angle: 0, tag: "Padded cup", note: "Padded cup sits with your projection." }) },
  { re: /underband|\bband\b/,
    step: () => ({ feature: "underband", sub: "center", angle: 0, tag: "Underband", note: "Broad band carries the support." }) },
  { re: /\bstrap/,
    step: () => ({ feature: "cups", sub: "strap", angle: 0, tag: "Straps", note: "Straps fine-tune the lift." }) },
  { re: /zip/,
    step: () => ({ feature: "cups", sub: "center", angle: 0, tag: "Front zip", note: "Front-zip closure." }) },
  { re: /racerback|cross[- ]?back/,
    step: () => ({ feature: "edge", sub: "center", angle: 180, tag: "Racerback", note: "Racerback shares the load." }) },
];

// 1820 (210) vocabulary → the SAME anchors the 1820 body carries, but with
// 1820-true labels. cups/center is the MESH panel here (not a zip); cups/apex is
// the cotton-lined non-padded cup; edge is the hook-and-eye back (not a racerback).
// So when the Your Fit chat mentions a 1820 feature, the pin that lights up and
// its callout both match what the shopper is reading.
const ANSWER_FEATURES_1820 = [
  { re: /gap|gapp|spill|full[- ]?coverage|coverage|neckline|top[- ]?edge|upper[- ]?cup|sits high|no gaping/,
    step: () => ({ feature: "cups", sub: "coverage", angle: 0, tag: "Full coverage", note: "Sits high on the chest — full coverage, no gaping." }) },
  { re: /cotton|cotton[- ]?lined|non[- ]?padded|unpadded|no[- ]?apex|apex[- ]?show|show[- ]?through|plush|lining|soft[- ]?touch|moulded|molded|shape/,
    step: () => ({ feature: "cups", sub: "apex", angle: 0, tag: "Cotton-lined cup", note: "Soft cotton-lined cup — shapes you with no apex show-through." }) },
  { re: /mesh|front[- ]?panel|\bpanel\b|stylised|styled|breathab/,
    step: () => ({ feature: "cups", sub: "center", angle: 0, tag: "Mesh front panel", note: "Stylised front mesh panel — breathable, keeps its shape." }) },
  { re: /\bstrap|cushion|shoulder|dig/,
    step: () => ({ feature: "cups", sub: "strap", angle: 0, tag: "Cushioned straps", note: "Broad cushioned, adjustable straps — no shoulder dig." }) },
  { re: /underband|\bband\b|wire[- ]?free|no[- ]?wire|support|even pressure/,
    step: () => ({ feature: "underband", sub: "center", angle: 0, tag: "Broad wirefree band", note: "Wide wirefree band spreads support evenly — no wire." }) },
  { re: /\bback\b|hook|hook[- ]?and[- ]?eye|closure|adjustab/,
    step: () => ({ feature: "edge", sub: "center", angle: 180, tag: "Adjustable back", note: "Hook-and-eye back adjusts the band as you wear it in." }) },
];

// 1841 (212) — Deep Taupe MINIMISER. Same anchors, minimiser-true labels.
const ANSWER_FEATURES_1841 = [
  { re: /gap|gapp|spill|full[- ]?coverage|coverage|smooth|neckline|top[- ]?edge|upper[- ]?cup/,
    step: () => ({ feature: "cups", sub: "coverage", angle: 0, tag: "Full coverage", note: "Full coverage smooths the bustline — no spillage." }) },
  { re: /minimis|minimiz|reduce|smaller|profile|projection|1[- ]?inch|silhouette|flatten/,
    step: () => ({ feature: "cups", sub: "apex", angle: 0, tag: "Minimiser cup", note: "Minimiser cup visually reduces the bust by up to 1 inch." }) },
  { re: /\bstrap|cushion|shoulder|dig/,
    step: () => ({ feature: "cups", sub: "strap", angle: 0, tag: "Cushioned straps", note: "Broad cushioned straps — all-day shoulder comfort." }) },
  { re: /underband|\bband\b|\bside\b|reinforce|wire[- ]?free|support|hold/,
    step: () => ({ feature: "underband", sub: "center", angle: 0, tag: "Broad underband", note: "Wide band with side reinforcement — steady hold and smoothing." }) },
  { re: /\bback\b|bulge|closure|hook/,
    step: () => ({ feature: "edge", sub: "center", angle: 180, tag: "Smooth back", note: "Smooths across the back — no band bulge." }) },
];

// MI16 (216) — Grape Wine racerback sports bra. Same Round_D34 anchors, MI16-true
// labels: padded (not cotton-lined) cup, StayDry soft-touch microfiber at the cup
// centre (not a mesh panel or zip), adjustable cushioned straps, broad underband,
// and a true racerback (edge @180). Verified against the PDP feature list.
const ANSWER_FEATURES_MI16 = [
  { re: /gap|gapp|spill|full[- ]?coverage|coverage|neckline|top[- ]?edge|upper[- ]?cup|sits high|no gaping|containment/,
    step: () => ({ feature: "cups", sub: "coverage", angle: 0, tag: "Full coverage", note: "High square neckline — full coverage, no spill during movement." }) },
  { re: /padded|padding|cup depth|\bdepth\b|moulded|molded|shape|hold|wirefree|no[- ]?wire|bounce|impact|support/,
    step: () => ({ feature: "cups", sub: "apex", angle: 0, tag: "Padded cup", note: "Wirefree padded cup gently shapes and holds — no wire to dig in." }) },
  { re: /staydry|stay[- ]?dry|stayfresh|stay[- ]?fresh|sweat|wick|moisture|breathab|microfiber|micro[- ]?fibre|soft[- ]?touch|fabric|dry|fresh|odour|odor|antimicrob|anti[- ]?microb/,
    step: () => ({ feature: "cups", sub: "center", angle: 0, tag: "StayDry fabric", note: "Soft-touch microfiber — StayDry wicks sweat, StayFresh stays odour-free." }) },
  { re: /\bstrap|cushion|shoulder|dig|adjust/,
    step: () => ({ feature: "cups", sub: "strap", angle: 0, tag: "Adjustable straps", note: "Adjustable cushioned straps — set your own fit, no shoulder dig." }) },
  { re: /underband|\bband\b|broad|extra support|anchor/,
    step: () => ({ feature: "underband", sub: "center", angle: 0, tag: "Broad underband", note: "Broad underband anchors the support for mid–high impact." }) },
  { re: /racerback|racer|cross[- ]?back|\bback\b|mobility|movement|move/,
    step: () => ({ feature: "edge", sub: "center", angle: 180, tag: "Racerback", note: "Racerback moves with you — full shoulder mobility, stays put." }) },
];

// Answer-feature table for the product. 1820 (210), 1841 (212) and MI16 (216) have
// their own vocabulary + labels; everything else uses the MI10 default set.
function answerFeaturesFor(product) {
  const p = String(product || "");
  if (p === "210" || p === "1820") return ANSWER_FEATURES_1820;
  if (p === "212" || p === "1841") return ANSWER_FEATURES_1841;
  if (p === "216" || p === "MI16") return ANSWER_FEATURES_MI16;
  return ANSWER_FEATURES;
}

// Build a hotspot sequence from the answer text (falls back to the question map
// when there's no answer or nothing matched). Capped at 3 steps so the callout
// stays brief.
export function sequenceForAnswer(question, answer, product) {
  const a = (answer || "").toLowerCase();
  if (a) {
    const hits = [];
    for (const f of answerFeaturesFor(product)) {
      const m = a.search(f.re);
      if (m >= 0) hits.push({ i: m, step: f.step() });
    }
    hits.sort((x, y) => x.i - y.i);
    const seq = hits.slice(0, 3).map((h) => h.step);
    if (seq.length) return seq;
  }
  return sequenceForQuestion(question);
}
